import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HumisLogo } from '@/components/brand/HumisLogo'
import { apiClient } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'

/**
 * Where the emailed reset link lands: a new password, twice.
 *
 * <p>Twice because a typo here would lock somebody out of an account they came to get back into.
 * What the password policy refuses is shown under the box, in the server's own words, rather
 * than as a banner — the person needs to know which rule, not that one exists. A link that has
 * expired or been used gets one plain sentence and a way to ask for another.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  async function submit() {
    setError(null)
    setPasswordError(null)
    if (password !== confirm) {
      setPasswordError(t('auth.passwordsDiffer'))
      return
    }
    setSaving(true)
    try {
      await apiClient.post('/v1/auth/reset-password', { token, newPassword: password })
      setDone(true)
    } catch (caught) {
      const problem = formProblem(caught, 'That did not work. Ask for a new link and try again.')
      setPasswordError(problem.fields.newPassword ?? null)
      setError(problem.fields.newPassword ? null : problem.summary)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-[var(--color-page)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <HumisLogo variant="full" alt="HUMIS" className="h-16 w-auto max-w-full" />
        </div>
        <div className="hrms-card p-6">
          <h1 className="text-lg font-semibold text-[var(--color-heading)]">{t('auth.resetTitle')}</h1>

          {!token && (
            <div className="mt-3 space-y-3">
              <p role="alert" className="text-sm text-[var(--color-danger)]">{t('auth.resetLinkIncomplete')}</p>
              <Link to="/forgot-password" className="text-sm text-[var(--color-primary)] hover:underline">
                {t('auth.requestNewLink')}
              </Link>
            </div>
          )}

          {done && (
            <div className="mt-4 space-y-4">
              <p role="status" className="flex items-start gap-2 text-sm text-[var(--color-success)]">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {t('auth.resetDone')}
              </p>
              <Button asChild className="w-full">
                <Link to="/login">{t('auth.signIn')}</Link>
              </Button>
            </div>
          )}

          {token && !done && (
            <form
              className="mt-4 space-y-4"
              noValidate
              onSubmit={(event) => { event.preventDefault(); void submit() }}
            >
              {error && (
                <div
                  role="alert"
                  className="space-y-2 rounded-[var(--radius-control)] bg-[var(--color-tint-red)] px-3 py-2 text-sm text-[var(--color-danger)]"
                >
                  <p className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </p>
                  <Link to="/forgot-password" className="block text-[var(--color-primary)] hover:underline">
                    {t('auth.requestNewLink')}
                  </Link>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="reset-password" required>{t('auth.newPassword')}</Label>
                <Input
                  id="reset-password" type="password" autoComplete="new-password" required
                  value={password} invalid={Boolean(passwordError)}
                  aria-describedby={passwordError ? 'reset-password-error' : undefined}
                  onChange={(event) => setPassword(event.target.value)}
                />
                {passwordError && (
                  <p id="reset-password-error" className="text-xs text-[var(--color-danger)]">{passwordError}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reset-confirm" required>{t('auth.confirmPassword')}</Label>
                <Input
                  id="reset-confirm" type="password" autoComplete="new-password" required
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={saving || !password || !confirm}>
                {saving ? t('auth.sending') : t('auth.setPassword')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
